import React, { useState, useLayoutEffect, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import { useRouteMatch } from 'react-router-dom';
import { sanitize } from 'dompurify';

import styles from './styles.module.css';
import { getCurrenLang } from '../../redux/selector';
import {
  useHcpSendPinCode,
  useCheckPinCodeData,
  useSignUpCreate,
  useCheckInvite,
  useResentInvite,
} from '../../graphql/auth/useSignUp';
import { SignUpAccountVerified, SignUpAccount, SignUpAccountCreated } from '../Form/SignUp';
import {
  validateCreateHcpUser,
  validationCreateHcpUser,
  validationEmail,
  validationPIN,
} from '../../utils/validators';
import { initialCode, initialValuesSingUp } from './initValues';
import { SectionTitle, Loading, NotificationModal, FormFormik, Confirmation } from '../../common';
import { parseJwt, path, storage } from '../../utils';
import { delay } from '../../utils/helper';
import { FormValues, FormValuesCode } from './types';
import LogoPublic from '../../common/Logo/LogoPublic';
import Bubble from '../../common/Logo/Bubble';

export const CreateAnAccount = (): JSX.Element => {
  const t: any = useSelector<any>((state) => getCurrenLang(state));
  const resend_pin_code_message = t?.validation.resend_pin_code_message;
  const sign_up = t?.title.sign_up;
  const link_expired = t?.notifications.link_expired;
  const get_new_invite = t?.notifications.get_new_invite;
  const new_invite = t?.common.new_invite;
  const invite_link_title = t?.notifications.invite_link_title;
  const invite_link_context = t?.notifications.invite_link_context;
  const pin_code_is_going_to_be_sent_to_for_HCP = t?.auth.auth_pin_code_to_phone_and_email;

  // Endpoints
  const { _checkInviteToken, dataInvite, errorInvite, loading } = useCheckInvite();
  const { _sendInvite, resentInviteData, errorInviteData, resentInviteLoading } = useResentInvite();
  const { _sendPinCode, dataPinCode, errorPinCode, loadingSensPin } = useHcpSendPinCode();
  const {
    _sendVarefiredPin,
    dataVarefiredPin,
    errorVarefiredCode,
    checkPinLoading,
  } = useCheckPinCodeData();
  const { _signUp, dataSignUp, errorSignUp, loadingSignUp } = useSignUpCreate();

  // Local state
  const [query, setQuery] = useState('');
  const [email, setEmail] = useState('');
  const [dataVarefiredToken, setDataVarefiredToken] = useState('');
  const [showModalWithEmail, setShowModalWithEmail] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resendPending, setResendPending] = useState(false);

  const { params } = useRouteMatch();

  const sendPinToUser = (): void => {
    _sendPinCode(query && query);
  };

  useEffect(() => {
    if (dataPinCode) {
      setShowModalWithEmail(true);
    } else if (errorPinCode) {
      setShowModalWithEmail(false);
    }
  }, [dataPinCode, errorPinCode]);

  const sendCode = (paramets: FormValuesCode, { resetForm }: any): void => {
    const pinCode = paramets ? paramets.code.trim() : '';
    const data = {
      inviteToken: query,
      pinCode,
    };
    _sendVarefiredPin(data);
    resetForm();
  };

  useEffect(() => {
    if (dataVarefiredPin) {
      setVerified(true);
      setDataVarefiredToken(dataVarefiredPin.inviteToken);
    } else if (errorVarefiredCode) {
      setVerified(false);
    }
  }, [dataVarefiredPin]);

  const send = (user: FormValues): void => {
    _signUp(user, dataVarefiredToken);
  };

  const onResendPin = async (): Promise<void> => {
    _sendPinCode(query && query);
    setResendPending(true);
    await delay(30000).then((res: any) => setResendPending(res.message));
  };

  const _onResend = (): void => {
    const { token }: any = params;
    if (token) {
      _sendInvite(token);
    }
  };

  useLayoutEffect(() => {
    const { token }: any = params;
    if (token) {
      _checkInviteToken(token);
      setQuery(token);
    }
  }, [params]);

  useEffect(() => {
    const data = dataInvite && dataInvite.email;
    if (data) {
      setEmail(data);
    }
  }, [dataInvite]);

  useEffect(() => {
    if (dataSignUp) {
      const token = dataSignUp.access_token;
      const { refresh_token } = dataSignUp;

      const { email: parsEmail, id, role, lang } = parseJwt(token);
      const setUser = {
        parsEmail,
        lang,
        id,
        role,
        token,
        refresh_token,
      };
      storage.save('user', setUser);
      storage.save('lang', lang);
      window.location.assign(path.home);
    }
  }, [dataSignUp]);

  const emailAccont = !verified && !showModalWithEmail && email && !loadingSensPin && (
    <Formik
      initialValues={{ email }}
      onSubmit={sendPinToUser}
      validationSchema={(): any => validationEmail(t)}
      component={SignUpAccount}
    />
  );

  // Normalise text message
  const getText = (response: { phone: string; email: string }): string => {
    const phone = response?.phone || '';
    const emailFromResponse = response?.email || '';
    let pin_code_is_going_to_be_sent_to_your_email = '';
    pin_code_is_going_to_be_sent_to_your_email = pin_code_is_going_to_be_sent_to_for_HCP
      .replace('<phone>', `<b>${phone}</b>`)
      .replace('<email>', `<b>${emailFromResponse}</b>`);

    return pin_code_is_going_to_be_sent_to_your_email;
  };

  const verifiedAccont = showModalWithEmail && !dataVarefiredPin && (
    <div className={styles.verifiedWrapper}>
      <div className={styles.section}>
        <p dangerouslySetInnerHTML={{ __html: sanitize(getText(dataPinCode)) }} />
      </div>
      <Formik
        initialValues={initialCode}
        onSubmit={sendCode}
        validationSchema={(): any => validationPIN(t)}
        render={(props): any => (
          <SignUpAccountVerified
            {...props}
            resendMethod={onResendPin}
            resendPending={resendPending}
            error={errorVarefiredCode}
            resendPinCodeMessage={resend_pin_code_message}
          />
        )}
      />
    </div>
  );
  const createAnAccont = verified && (
    <div className={styles.contant}>
      <FormFormik
        initialValues={initialValuesSingUp}
        schema={(): any => validationCreateHcpUser(t)}
        validate={(values: any): any => validateCreateHcpUser(values, t)}
        onSend={send}
        errorMessage={errorSignUp}
        component={SignUpAccountCreated}
      />
    </div>
  );

  const isSeccessToke = !errorInvite && (
    <>
      <SectionTitle title={sign_up} />
      {emailAccont}
      {verifiedAccont}
      {createAnAccont}
    </>
  );

  const isExpiredToke = errorInvite && !loading && !resentInviteData && (
    <NotificationModal
      onResend={_onResend}
      hasButton
      title={link_expired}
      contant={get_new_invite}
      buttonName={new_invite}
    />
  );
  const resendInviteJsx = resentInviteData && !errorInviteData && !resentInviteLoading && (
    <Confirmation icon="success" title={invite_link_title} contant={invite_link_context} />
  );

  const loadingJSX = (loading ||
    loadingSensPin ||
    checkPinLoading ||
    loadingSignUp ||
    resentInviteLoading) && <Loading />;

  return (
    <>
      {loadingJSX}
      <div className={styles.main_container}>
        <LogoPublic />
        {isSeccessToke}
        {isExpiredToke}
        {resendInviteJsx}
      </div>
      <Bubble />
    </>
  );
};
